/**
 * Header.jsx - Fixed navigation header for Curis Path
 * 
 * Features:
 * - Fixed position with blur backdrop
 * - Logo and navigation links
 * - WhatsApp button (opens chat with pre-filled message)
 * - Get Started CTA (scrolls to enquiry form)
 * 
 * @author Curis Path Development Team
 */

import React from 'react';
import { ArrowRight } from 'lucide-react';
import { contactInfo } from '../data/mock';

/**
 * WhatsApp SVG Icon Component
 * Uses the official WhatsApp logo design
 */
const WhatsAppIcon = ({ size = 18 }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Header = () => {
  /**
   * Smoothly scrolls to a section by ID
   * @param {string} sectionId - The ID of the target section
   */
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80; // Height of fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Format WhatsApp number (remove non-digits) and create chat link
  const whatsappNumber = contactInfo.whatsapp.replace(/[^0-9]/g, '');
  const whatsappMessage = encodeURIComponent(
    'Hi Curis Path, I would like to enquire about medical treatment in India.'
  );
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${whatsappMessage}`;

  return (
    <header className="header-nav">
      <div className="flex items-center justify-between w-full max-w-7xl mx-auto">
        
        {/* Logo */}
        <div className="flex items-center gap-3">
          <a 
            href="/" 
            className="text-2xl font-semibold" 
            style={{ color: 'var(--text-primary)', textDecoration: 'none' }}
          >
            Curis Path
          </a>
        </div>

        {/* Navigation Links - Hidden on mobile */}
        <nav className="hidden md:flex items-center gap-6">
          {['specialties', 'hospitals', 'doctors', 'testimonials'].map((section) => (
            <button 
              key={section}
              onClick={() => scrollToSection(section)} 
              className="body-small font-medium hover:opacity-70 transition-opacity capitalize"
              style={{ color: 'var(--text-primary)' }}
            >
              {section}
            </button>
          ))}
        </nav>

        {/* CTA Buttons */}
        <div className="flex items-center gap-3">
          
          {/* WhatsApp Button */}
          <a 
            href={whatsappLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2.5 rounded-full font-medium text-sm transition-all hover:scale-105 hover:shadow-lg"
            style={{ 
              background: '#25D366',
              color: 'white',
              boxShadow: '0 2px 8px rgba(37, 211, 102, 0.3)'
            }}
            data-testid="header-whatsapp-btn"
            title="Chat with us on WhatsApp"
          >
            <WhatsAppIcon size={18} />
            <span className="hidden sm:inline">WhatsApp Us</span>
          </a>
          
          {/* Get Started Button */}
          <button 
            onClick={() => scrollToSection('enquiry')} 
            className="flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm text-white transition-all hover:scale-105 hover:shadow-lg"
            style={{ 
              background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8E53 50%, #FFC853 100%)',
              boxShadow: '0 4px 15px rgba(255, 107, 107, 0.4)'
            }}
            data-testid="header-get-started-btn"
          >
            <span>Get Started</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
